import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, AlertCircle, Loader2, CheckCircle, Eye, EyeOff, XCircle } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { validateResetToken, resetPassword, getErrorMessage } from '../services/authService';
import { validatePassword, validatePasswordConfirmation, getPasswordValidationErrors, getPasswordStrength } from '../utils/validation';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; confirm?: string }>({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenError, setTokenError] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validate token on mount
  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setTokenError('No reset token provided. Please use the link from your email.');
        setIsValidating(false);
        return;
      }

      try {
        const response = await validateResetToken(token);
        if (!response.isValid) {
          setTokenError(response.errorMessage || 'This password reset link is invalid or has expired.');
        } else if (response.email) {
          setUserEmail(response.email);
        }
      } catch (err) {
        setTokenError('Unable to validate reset link. Please try again.');
      } finally {
        setIsValidating(false);
      }
    };

    checkToken();
  }, [token]);

  const passwordErrors = getPasswordValidationErrors(newPassword);
  const passwordStrength = getPasswordStrength(newPassword);

  const validateForm = (): boolean => {
    const errors: { password?: string; confirm?: string } = {};

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.error;
    }

    const confirmValidation = validatePasswordConfirmation(newPassword, confirmPassword);
    if (!confirmValidation.isValid) {
      errors.confirm = confirmValidation.error;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword({
        token,
        newPassword,
        confirmPassword,
      });
      setIsSuccess(true);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getStrengthColor = () => {
    switch (passwordStrength) {
      case 'strong':
        return 'bg-green-500';
      case 'medium':
        return 'bg-yellow-500';
      default:
        return 'bg-red-500';
    }
  };

  const getStrengthWidth = () => {
    switch (passwordStrength) {
      case 'strong':
        return 'w-full';
      case 'medium':
        return 'w-2/3';
      default:
        return 'w-1/3';
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-gray-800/20 via-gray-700/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-black via-gray-900/50 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-br from-gray-900/10 to-gray-800/10 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <Navigation variant="dark" />

      {/* Content */}
      <div className="relative z-10 flex items-center justify-center px-4 py-8 pt-32" style={{minHeight: 'calc(100vh - 120px)'}}>
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-black mb-4 text-white">
              Reset Password
            </h1>
            <p className="text-lg text-slate-400">
              {isValidating ? 'Validating your reset link...' :
               tokenError ? 'Unable to reset password' :
               'Create a new secure password'}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            {isValidating ? (
              // Loading State
              <div className="text-center py-8">
                <Loader2 className="w-12 h-12 text-slate-400 animate-spin mx-auto mb-4" />
                <p className="text-slate-400">Validating reset link...</p>
              </div>
            ) : tokenError ? (
              // Token Error State
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-red-400" />
                </div>
                <h2 className="text-xl font-bold mb-2 text-white">Invalid Reset Link</h2>
                <p className="text-slate-400 mb-6">{tokenError}</p>
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 rounded-xl transition-all font-bold text-white"
                >
                  Request New Link
                </Link>
                <div className="mt-4">
                  <Link
                    to="/login"
                    className="inline-flex items-center space-x-2 text-slate-400 hover:text-white transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Login</span>
                  </Link>
                </div>
              </div>
            ) : isSuccess ? (
              // Success State
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-xl font-bold mb-2 text-white">Password Reset Successfully</h2>
                <p className="text-slate-400 mb-6">
                  Your password has been updated. You can now sign in with your new password.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 rounded-xl transition-all font-bold text-white"
                >
                  Sign In
                </Link>
              </div>
            ) : (
              // Form State
              <>
                {userEmail && (
                  <div className="mb-6 p-4 bg-slate-800/50 rounded-xl">
                    <p className="text-sm text-slate-400">
                      Resetting password for: <span className="text-white font-medium">{userEmail}</span>
                    </p>
                  </div>
                )}

                {/* Error Alert */}
                {error && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-red-200">{error}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* New Password Field */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">
                      <Lock className="w-4 h-4 inline mr-2" />
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          if (fieldErrors.password) setFieldErrors({ ...fieldErrors, password: undefined });
                        }}
                        disabled={isLoading}
                        className="w-full px-4 py-3 pr-12 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#AD0000]/50 focus:ring-2 focus:ring-[#AD0000]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{borderColor: fieldErrors.password ? 'rgba(239, 68, 68, 0.5)' : undefined}}
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isLoading}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed p-1 rounded-lg bg-white/5 hover:bg-white/10"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>

                    {/* Password Strength Indicator */}
                    {newPassword && (
                      <div className="mt-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-slate-400">Password strength</span>
                          <span className={`text-xs font-medium capitalize ${
                            passwordStrength === 'strong' ? 'text-green-400' :
                            passwordStrength === 'medium' ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {passwordStrength}
                          </span>
                        </div>
                        <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                          <div className={`h-full transition-all duration-300 ${getStrengthColor()} ${getStrengthWidth()}`}></div>
                        </div>
                      </div>
                    )}

                    {/* Password Requirements */}
                    {newPassword && passwordErrors.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {passwordErrors.map((err, idx) => (
                          <p key={idx} className="text-xs text-red-400 flex items-center">
                            <XCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                            {err}
                          </p>
                        ))}
                      </div>
                    )}

                    {fieldErrors.password && (
                      <p className="mt-2 text-sm text-red-400">{fieldErrors.password}</p>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">
                      <Lock className="w-4 h-4 inline mr-2" />
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (fieldErrors.confirm) setFieldErrors({ ...fieldErrors, confirm: undefined });
                        }}
                        disabled={isLoading}
                        className="w-full px-4 py-3 pr-12 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#AD0000]/50 focus:ring-2 focus:ring-[#AD0000]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{borderColor: fieldErrors.confirm ? 'rgba(239, 68, 68, 0.5)' : undefined}}
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        disabled={isLoading}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed p-1 rounded-lg bg-white/5 hover:bg-white/10"
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>

                    {/* Password Match Indicator */}
                    {confirmPassword && (
                      <p className={`mt-2 text-xs flex items-center ${
                        newPassword === confirmPassword ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {newPassword === confirmPassword ? (
                          <>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Passwords match
                          </>
                        ) : (
                          <>
                            <XCircle className="w-3 h-3 mr-1" />
                            Passwords do not match
                          </>
                        )}
                      </p>
                    )}

                    {fieldErrors.confirm && (
                      <p className="mt-2 text-sm text-red-400">{fieldErrors.confirm}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="group w-full px-6 py-4 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 hover:scale-105 rounded-xl transition-all duration-250 ease-spring shadow-xl shadow-red-500/30 hover:shadow-glow-red-lg flex items-center justify-center space-x-2 font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Resetting Password...</span>
                      </>
                    ) : (
                      <span>Reset Password</span>
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center space-x-2 text-slate-400 hover:text-white transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Login</span>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ResetPassword;
